import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronUp from '@apollo/icons/default/IconChevronUp.svg';
import IconSuccessSolid from '@apollo/icons/default/IconSuccessSolid.svg';
import IconInfo from '@apollo/icons/small/IconInfo.svg';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
} from '@apollo/orbit';
import { FormikHelpers } from 'formik';
import { printSchema } from 'graphql';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { HeaderEntry } from 'src/app/graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/shared';
import { headersObjectFromDefinitions } from 'src/app/graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import {
  IntrospectionFailure,
  useSchemaFromEndpointIntrospection,
} from 'src/app/graph/explorerPage/hooks/useExplorerState/useSchema/useSchemaFromEndpointIntrospection/useSchemaFromEndpointIntrospection';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { URLField } from 'src/components/urlField/URLField';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCheckPlanTypes } from 'src/hooks/useCheckPlanTypes';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { API_NAME_MAX_CHAR } from 'src/lib/graphOnboarding/constants';
import { isLocalUrl } from 'src/lib/isLocalUrl';

import { InputCharCount } from '../../../../../components/InputCharCount';
import { LocalIntrospectionModal } from '../advancedOptions/localIntrospectionModal/LocalIntrospectionModal';
import { getIntrospectionHelper } from '../introspectionHelper/getIntrospectionHelper';
import { CommonStepProps, NewSupergraphOnboardingFields } from '../shared';

// `React.lazy` assumes we're always using default exports and will internally
// try to access `.default` property of the result. We have to use this
// workaround to use named exports.
// @see https://github.com/reactjs/rfcs/pull/64#issuecomment-431507924
const UploadSchemaModal = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "UploadSchemaModal" */ '../advancedOptions/uploadSchemaModal/UploadSchemaModal'
    )
  ).UploadSchemaModal,
}));
const UploadHeadersModal = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "UploadHeadersModal" */ '../advancedOptions/uploadHeadersModal/UploadHeadersModal'
    )
  ).UploadHeadersModal,
}));

export const UnregisteredGraphTabContent = ({
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  validateField,
  handleBlur,
  setLoading,
  setValues,
  schemaUploaded,
  setSchemaUploaded,
  headers,
  setHeaders,
  advancedOptionsDrawerOpen,
  setAdvancedOptionsDrawerOpen,
  isUsingLocalIntrospection,
  setIsUsingLocalIntrospection,
}: Omit<
  CommonStepProps,
  | 'loading'
  | 'setContinueDisabled'
  | 'creatingSupergraph'
  | 'continueAnalyticsLabelsByStep'
  | 'setWhenStepsBegan'
  | 'whenStepsBegan'
  | 'createSupergraph'
  | 'createGraphError'
  | 'createGraphValidationError'
> & {
  schemaUploaded: boolean;
  setSchemaUploaded: React.Dispatch<React.SetStateAction<boolean>>;
  headers: HeaderEntry[];
  setHeaders: React.Dispatch<React.SetStateAction<HeaderEntry[]>>;
  advancedOptionsDrawerOpen: boolean;
  setAdvancedOptionsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUsingLocalIntrospection: React.Dispatch<React.SetStateAction<boolean>>;
  isUsingLocalIntrospection: boolean;
  setFieldTouched: FormikHelpers<NewSupergraphOnboardingFields>['setFieldTouched'];
}) => {
  const [currentAccountId] = useCurrentAccountId();
  const currentPlanResult = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });
  const { isDedicatedPlan } = useCheckPlanTypes({
    tier: currentPlanResult?.currentPlan?.tier,
    kind: currentPlanResult?.currentPlan?.kind,
  });
  const stableHeaders = useMemo(
    () => headersObjectFromDefinitions(headers),
    [headers],
  );

  const {
    schema: schemaFromIntrospection,
    subgraphSdl: subgraphSdlFromIntrospection,
    isLoading,
    introspectionFailureType,
    introspectionResponseErrors,
  } = useSchemaFromEndpointIntrospection({
    includeCookies: 'cycle-on-unreachable',
    pollInterval: 3000,
    uri: values.unregisteredEndpointUrl,
    stableHeaders,
    skipPolling:
      isDedicatedPlan || !!errors.unregisteredEndpointUrl || schemaUploaded,
    pollForSubgraphSdlFirst: true,
    updateOnConfigChange: true,
  });

  const sdlFromIntrospection = useMemo(
    () =>
      subgraphSdlFromIntrospection ??
      (schemaFromIntrospection
        ? printSchema(schemaFromIntrospection)
        : undefined),
    [schemaFromIntrospection, subgraphSdlFromIntrospection],
  );

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  const [showHeadersEditorModal, setShowHeadersEditorModal] = useState(false);
  const [showUploadSchemaModal, setShowUploadSchemaModal] = useState(false);
  const [showLocalIntrospectionModal, setShowLocalIntrospectionModal] =
    useState(false);

  useEffect(() => {
    // set the sdl based on endpoint introspection
    // only if user hasn't manually uploaded
    if (!schemaUploaded) {
      if (sdlFromIntrospection && !introspectionFailureType) {
        setFieldValue('sdl', sdlFromIntrospection, true);
      } else {
        setFieldValue('sdl', '');
      }
    }
  }, [
    introspectionFailureType,
    sdlFromIntrospection,
    schemaUploaded,
    setFieldValue,
    values.unregisteredEndpointUrl,
  ]);

  // This useEffect is necessary because we programmatically
  // set the unregisteredSubgraphName in the onChange of the endpoint url field
  // so the user can click 'next' without focusing & unfocusing the api name field
  useEffect(() => {
    if (
      values.unregisteredSubgraphName.length > 0 &&
      !touched.unregisteredSubgraphName
    ) {
      validateField('unregisteredSubgraphName');
    }
  }, [
    touched.unregisteredSubgraphName,
    validateField,
    values.unregisteredSubgraphName.length,
  ]);

  const clearOtherTab = useCallback(() => {
    setFieldValue('registeredVariantSubgraphName', '');
    setFieldTouched('registeredVariantSubgraphName', false);
    setFieldValue('registeredGraphRef', '');
    setFieldTouched('selectedRegisteredVariant', false);
  }, [setFieldTouched, setFieldValue]);

  const trackCustomEvent = useTrackCustomEvent();

  useEffect(() => {
    if (
      !isLoading &&
      introspectionFailureType !== 'unableToReachEndpoint' &&
      introspectionFailureType !== 'noEndpointProvided'
    ) {
      trackCustomEvent({
        action: 'endpoint introspection',
        category: '6M2S: Onboarding',
        label: `Introspection ${
          introspectionFailureType === 'introspectionDisabled'
            ? 'disabled'
            : !introspectionFailureType
              ? 'successful'
              : 'went wrong'
        } on user's endpoint. ${headers.length} Headers added.`,
      });
    }
  }, [headers.length, introspectionFailureType, isLoading, trackCustomEvent]);

  const introspectionHelper = useMemo(
    () =>
      getIntrospectionHelper({
        schemaUploaded,
        introspectionFailureType,
        isUsingLocalIntrospection,
        isLoading,
        routingUrl: values.unregisteredEndpointUrl,
        introspectionResponseErrors,
        sdlFromIntrospection,
        subgraphSdlFromIntrospection,
      }),
    [
      introspectionFailureType,
      introspectionResponseErrors,
      isLoading,
      isUsingLocalIntrospection,
      schemaUploaded,
      sdlFromIntrospection,
      subgraphSdlFromIntrospection,
      values,
    ],
  );

  const endpointIsLocal = useMemo(
    () => isLocalUrl(values.unregisteredEndpointUrl),
    [values.unregisteredEndpointUrl],
  );

  // we show 👀 and open the drawer when folks need to manually upload schema
  useEffect(() => {
    if (
      // we show 👀 and open the drawer when folks need to manually upload schema
      introspectionFailureType === 'introspectionDisabled' ||
      // we open the drawer when folks might need to add headers
      introspectionFailureType === 'invalidIntrospectionResponse' ||
      introspectionFailureType === 'invalidIntrospectionSchema' ||
      endpointIsLocal
    ) {
      setAdvancedOptionsDrawerOpen(true);
    }
  }, [endpointIsLocal, introspectionFailureType, setAdvancedOptionsDrawerOpen]);

  return (
    <>
      <div className="mb-2 mt-4 block text-base text-secondary lg:hidden">
        Your GraphQL API will become the first{' '}
        <span className="font-semibold">subgraph</span> in the new supergraph
        we’re about to create. If you have multiple APIs, don’t worry—you can
        add more later.
      </div>
      <FormControl
        className="mt-4"
        isRequired
        isInvalid={
          (touched.unregisteredEndpointUrl &&
            !!errors.unregisteredEndpointUrl) ||
          introspectionHelper?.type === 'error'
        }
      >
        <FormLabel>
          Endpoint URL{' '}
          <Tooltip
            className="w-72"
            label="Your supergraph's router will use this URL to query your API. We'll also use the URL once now to introspect your API and fetch its schema. If your API has introspection disabled, you can upload your schema under Advanced options."
          >
            <IconInfo tabIndex={0} data-testid="focusable-info-endpoint-url" />
          </Tooltip>
        </FormLabel>
        <URLField
          onBlur={handleBlur}
          // only don't auto focus so folks can choose to open the advanced options
          // dropdown and upload a schema instead of use the endpoint
          autoFocus={false}
          name="unregisteredEndpointUrl"
          onChange={(value) => {
            clearOtherTab();
            setValues(
              (fields) => ({
                ...fields,
                unregisteredEndpointUrl: value,
              }),
              // validate on change only when there are errors
              // Why? Because users should see errors go away asap if they fixed them,
              // they should not have to wait for blur
              'unregisteredEndpointUrl' in errors,
            );
            // on endpoint change, try to prefill the subgraph name as well
            try {
              const url = new URL(value);
              if (!touched.unregisteredSubgraphName) {
                const subdomainToFill = url.hostname.split('.')[0];
                setFieldValue(
                  'unregisteredSubgraphName',
                  decodeURIComponent(subdomainToFill)
                    .replaceAll(/[^a-zA-Z0-9_-]/g, '-')
                    .substring(0, API_NAME_MAX_CHAR),
                );
              }
            } catch {
              // do nothing, this is just an opportunistic side effect
            }
          }}
          placeholder="Add your GraphQL endpoint"
          value={values.unregisteredEndpointUrl}
          isSocket={false}
        />
        {introspectionHelper?.type === 'helper' ? (
          <FormHelperText>
            {introspectionHelper.icon}
            {introspectionHelper.message}
          </FormHelperText>
        ) : null}
        {introspectionHelper?.type === 'error' ? (
          <FormErrorMessage>
            {(touched.unregisteredEndpointUrl
              ? errors.unregisteredEndpointUrl
              : undefined) ??
              (introspectionHelper?.type === 'error' &&
                introspectionHelper.message)}
          </FormErrorMessage>
        ) : (
          <FormErrorMessage>{errors.unregisteredEndpointUrl}</FormErrorMessage>
        )}
      </FormControl>
      <FormControl
        className="mt-8"
        isRequired
        isInvalid={
          !!errors.unregisteredSubgraphName &&
          !!touched.unregisteredSubgraphName
        }
      >
        <FormLabel>
          Subgraph name
          <Tooltip label="This will be the name of your subgraph. Enter a name that reflects the data or capabilities that your API provides.">
            <IconInfo tabIndex={0} data-testid="focusable-info-subgraph-name" />
          </Tooltip>
        </FormLabel>
        <Input
          onBlur={handleBlur}
          onChange={(e) => {
            clearOtherTab();
            setValues(
              (fields) => ({
                ...fields,
                unregisteredSubgraphName: e.target.value,
              }),
              // validate on change only when there are errors
              'unregisteredSubgraphName' in errors,
            );
          }}
          value={values.unregisteredSubgraphName}
          name="unregisteredSubgraphName"
          placeholder="Give your new subgraph a name"
          rightElement={
            <InputCharCount
              count={values.unregisteredSubgraphName.length}
              maxCount={API_NAME_MAX_CHAR}
            />
          }
        />
        <FormErrorMessage>{errors.unregisteredSubgraphName}</FormErrorMessage>
      </FormControl>
      <Button
        data-testid="supergraph-onboarding-advanced-options"
        variant="buttonLink"
        size="sm"
        className="mb-2 mt-8"
        onClick={() => setAdvancedOptionsDrawerOpen((prev) => !prev)}
        rightIcon={
          advancedOptionsDrawerOpen ? <IconChevronUp /> : <IconChevronDown />
        }
      >
        Advanced options
      </Button>
      {advancedOptionsDrawerOpen && (
        <AdvancedOptions
          endpointIsLocal={endpointIsLocal}
          headers={headers}
          isUsingLocalIntrospection={isUsingLocalIntrospection}
          schemaUploaded={schemaUploaded}
          setShowHeadersEditorModal={setShowHeadersEditorModal}
          setShowUploadSchemaModal={setShowUploadSchemaModal}
          setShowLocalIntrospectionModal={setShowLocalIntrospectionModal}
          introspectionError={introspectionFailureType}
        />
      )}
      {
        // These aren't shown unless you click into them,
        // so don't show a loading spinner on the page
      }
      <React.Suspense fallback={<></>}>
        <UploadHeadersModal
          headers={headers}
          setHeaders={setHeaders}
          onClose={() => setShowHeadersEditorModal(false)}
          isOpen={showHeadersEditorModal}
        />
        <UploadSchemaModal
          sdl={values.sdl}
          setSdl={(value) => setFieldValue('sdl', value, true)}
          setSchemaUploaded={(value) => {
            setSchemaUploaded(value);
            setIsUsingLocalIntrospection(false);
          }}
          onClose={() => setShowUploadSchemaModal(false)}
          isOpen={showUploadSchemaModal}
        />
        <LocalIntrospectionModal
          stableHeaders={stableHeaders}
          setSdl={(value) => setFieldValue('sdl', value, true)}
          setSchemaUploaded={(value) => {
            setSchemaUploaded(value);
            setIsUsingLocalIntrospection(true);
          }}
          onClose={() => setShowLocalIntrospectionModal(false)}
          isOpen={showLocalIntrospectionModal}
        />
      </React.Suspense>
    </>
  );
};

const AdvancedOptions = ({
  setShowHeadersEditorModal,
  headers,
  setShowUploadSchemaModal,
  setShowLocalIntrospectionModal,
  schemaUploaded,
  introspectionError,
  isUsingLocalIntrospection,
  endpointIsLocal,
}: {
  setShowHeadersEditorModal: (showHeadersEditorModal: boolean) => void;
  headers: HeaderEntry[];
  setShowUploadSchemaModal: (showUploadSchemaModal: boolean) => void;
  setShowLocalIntrospectionModal: (
    showLocalIntrospectionModal: boolean,
  ) => void;
  schemaUploaded: boolean;
  introspectionError?: IntrospectionFailure;
  isUsingLocalIntrospection: boolean;
  endpointIsLocal: boolean;
}) => {
  // TODO: These buttons don't follow the same Orbit button pattern and should be
  // a custom component. Leaving as Orbit button for now as we clean up space-kit buttons.
  return (
    <div className="w-full pb-10">
      <Button
        onClick={() => setShowHeadersEditorModal(true)}
        variant="hidden"
        className="my-3 mr-4 block h-fit w-full whitespace-pre-wrap rounded border border-primary bg-white p-3 text-left text-base"
      >
        <div className="font-semibold">Provide HTTP Headers</div>
        <div className="mx-auto font-normal">
          <div className="text-secondary">
            Choose this if your API requires authentication
          </div>
        </div>
        {headers.length > 0 &&
          headers[0]?.headerName &&
          headers[0].headerName.length > 0 && (
            <div className="mt-2 flex h-5 items-center text-sm font-normal">
              <>
                <IconSuccessSolid className="mr-2 !text-icon-success" />
                Headers added
              </>
            </div>
          )}
      </Button>
      <Button
        data-testid="supergraph-wizard-open-schema-modal-button"
        onClick={() => setShowUploadSchemaModal(true)}
        variant="hidden"
        className="my-3 block h-fit w-full whitespace-pre-wrap rounded border border-primary bg-white p-3 text-left text-base"
      >
        <div className="font-semibold">Provide your schema directly</div>
        <div className="mx-auto font-normal">
          <div className="text-secondary">
            Choose this if you'd like to upload or paste your schema
          </div>
        </div>
        {!isUsingLocalIntrospection &&
          (schemaUploaded ||
            introspectionError === 'introspectionDisabled') && (
            <div className="mt-2 flex h-5 items-center text-sm font-normal">
              {schemaUploaded ? (
                <>
                  <IconSuccessSolid className="mr-2 !text-icon-success" />
                  Schema uploaded
                </>
              ) : (
                introspectionError === 'introspectionDisabled' && (
                  <>👀 Please upload your schema</>
                )
              )}
            </div>
          )}
      </Button>
      <Button
        onClick={() => setShowLocalIntrospectionModal(true)}
        variant="hidden"
        className="my-3 block h-fit w-full whitespace-pre-wrap rounded border border-primary bg-white p-3 text-left text-base"
      >
        <div className="font-semibold">Use local introspection</div>
        <div className="mx-auto font-normal">
          <div className=" text-secondary">
            Choose this if you’d like Studio to fetch your schema locally
          </div>
        </div>
        {((schemaUploaded && isUsingLocalIntrospection) || endpointIsLocal) && (
          <div className="mt-2 flex h-5 items-center text-sm font-normal">
            <>
              {endpointIsLocal ? (
                <>👀 Please introspect your schema</>
              ) : (
                <>
                  <IconSuccessSolid className="mr-2 !text-icon-success" />
                  Schema fetched
                </>
              )}
            </>
          </div>
        )}
      </Button>
    </div>
  );
};
